import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { filter, Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { OPTIONS } from '../../../constants/options-list.constants';
import { IStyledOption } from '../../../constants/documents.constants';
import { DynamicProjectedTableComponent } from '../../../dynamic-projected-table/dynamic-projected-table.component';
import { ExpansionPanelComponent } from '../../../expansion-panel/expansion-panel.component';
import { PageLoadingComponent } from '../../../page-loading/page-loading.component';
import { FadedTextComponent } from '../../../faded-text/faded-text.component';
import { MoneyPipe } from '@app/pipes/framework/money-short.pipe';
import { primeDetailsSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { StatusBlockComponent } from '@app/pages/webapp/projects/view-project/rfi-listing/status-block/status-block.component';
import {
  COR_STATUS,
  CORDetail,
  CORLogItem,
} from '@app/store/prime-commitments/prime-commitments.types';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { DeepCopyService } from '@app/services/deep-copy.service';
import { CorMarkupsComponent } from '@app/framework/interaction-bar/prime-commitments/cor-details/cor-markups/cor-markups.component';
import { corDetailsActions } from '@app/store/prime-commitments/prime-commitments.actions';
import { CHANGE_ACTIONS_TYPE } from '@app/store/prime-commitments/prime-commitments.constants';
import { NotificationsService } from '@app/services/notifications.service';
import { StyledOptionsListComponent } from '@app/framework/overlays/styled-options-list/styled-options-list.component';
import { PcoItemCostExpanded } from '@app/store/pco/pco.types';
import { AttachmentsDropdownComponent } from '@app/framework/file-handling/attachments-dropdown/attachments-dropdown.component';

@Component({
  selector: 'app-cor-details',
  imports: [
    AsyncPipe,
    StyledOptionsListComponent,
    StatusBlockComponent,
    DynamicProjectedTableComponent,
    ExpansionPanelComponent,
    PageLoadingComponent,
    MoneyPipe,
    FadedTextComponent,
    CorMarkupsComponent,
    AttachmentsDropdownComponent,
  ],
  templateUrl: './cor-details.component.html',
  styleUrl: './cor-details.component.scss',
})
export class CorDetailsComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly interactionBar = inject(InteractionBarStateService);
  protected readonly OPTIONS = OPTIONS;
  protected readonly notif = inject(NotificationsService);

  private editChangeActionTriggered = false;
  protected COR: CORDetail;

  private readonly isDestroyed$ = new Subject<void>();

  @Input() CORLogItem: CORLogItem;

  protected readonly COR$ = this.store.select(primeDetailsSelectors.selectCORDetailsSidebar);

  protected readonly wasDataLoadedOnce$: Observable<boolean> = this.store.select(
    primeDetailsSelectors.selectWasCORDataLoadedOnce,
  );
  protected readonly isLoading$: Observable<boolean> = this.store.select(
    primeDetailsSelectors.selectIsCORDetailLoading,
  );

  editOptions: IStyledOption[] = [
    { value: OPTIONS.EDIT },
    { value: OPTIONS.MARK_APPROVED },
    { value: OPTIONS.MARK_REJECTED },
    { value: OPTIONS.DELETE },
  ];

  expandedPCOs = new Map<number, boolean>();
  expandedSOVs = new Map<string, boolean>();
  expandedPCOItems = new Map<number, boolean>();

  combinedCommitmentTotals$ = this.store.select(
    primeDetailsSelectors.selectCORDetailsCombinedCommitmentTotals,
  );
  totalDaysExtension$ = this.store.select(
    primeDetailsSelectors.selectCORDetailsTotalDurationExtension,
  );

  ngOnInit() {
    this.store.dispatch(corDetailsActions.onCORDetailsAccess({ id: this.CORLogItem.id }));
    this.COR$.pipe(
      takeUntil(this.isDestroyed$),
      filter((COR) => !!COR),
    ).subscribe((data) => {
      this.COR = DeepCopyService.deepCopy(data);
    });
  }

  ngOnDestroy() {
    this.store.dispatch(
      corDetailsActions.onCORDetailsSidebarClosed({
        editChangeActionTriggered: this.editChangeActionTriggered,
      }),
    );
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  async registerOption(option: OPTIONS) {
    switch (option) {
      case OPTIONS.EDIT: {
        this.editChangeActionTriggered = true;
        this.interactionBar.openChangeAction(true, CHANGE_ACTIONS_TYPE.CHANGE_ORDER_REQUEST);
        break;
      }
      case OPTIONS.DELETE: {
        const resp = await this.notif.showPopup('Are you sure you want to delete this COR?');
        if (resp) {
          this.store.dispatch(corDetailsActions.onDeleteCOR({ id: this.CORLogItem.id }));
        }
        break;
      }
      case OPTIONS.MARK_APPROVED: {
        this.COR.status = COR_STATUS.APPROVED;
        this.updateCORStore();
        break;
      }
      case OPTIONS.MARK_REJECTED: {
        this.COR.status = COR_STATUS.REJECTED;
        this.updateCORStore();
        break;
      }
    }
  }

  onDrop(event: CdkDragDrop<PcoItemCostExpanded[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      event.container.data.forEach((item, index: number) => {
        item.index = index;
      });
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      event.container.data.forEach((item, index: number) => {
        item.index = index;
      });
      event.previousContainer.data.forEach((item: PcoItemCostExpanded, index: number) => {
        item.index = index;
      });
    }

    this.updateCORStore();
  }

  updateCORStore() {
    this.store.dispatch(
      corDetailsActions.onCORLocalStateChanged({
        COR: this.COR,
      }),
    );
  }
}
